import React from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { Button } from "../../../components/forms/Buttons/Button";
import Input from "../../../components/forms/Input/Input";
import Select from "../../../components/forms/Select/Select";
import CustomDialog from "../../../components/modals/CustomDialog/CustomDialog";

export default function AccountForm({
  isOpen,
  isEdit,
  formData,
  setFormData,
  onClose,
  onSubmit,
  accountRoles = [],
}) {
  const [t] = useTranslation();

  // Check if user has PARTICIPANT role
  const isParticipant = accountRoles.some(
    (role) => role?.code === "PARTICIPANT"
  );

  // Format phone number - only allow 10 digits (without +7)
  const formatPhoneNumber = (value) => {
    if (!value) return "";
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");
    // Limit to 10 digits
    return digits.slice(0, 10);
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      lastName: formData.last_name || "",
      firstName: formData.first_name || "",
      middleName: formData.middle_name || "",
      email: formData.email || "",
      username: formData.username || "",
      phone_number: formData.phone_number || "",
      gender: formData.gender || "",
      grade: formData.grade || null,
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      reset({
        lastName: formData.last_name || "",
        firstName: formData.first_name || "",
        middleName: formData.middle_name || "",
        email: formData.email || "",
        username: formData.username || "",
        phone_number: formData.phone_number || "",
        gender: formData.gender || "",
        grade: formData.grade || null,
      });
    }
  }, [isOpen, formData, reset]);

  const handleClose = () => {
    reset();
    setFormData({
      first_name: "",
      last_name: "",
      middle_name: "",
      email: "",
      username: "",
      phone_number: "",
      gender: "",
      grade: null,
    });
    onClose();
  };

  const onFormSubmit = (data) => {
    console.log(data);
    const phoneWithPrefix = data.phone_number
      ? `+7${data.phone_number}`
      : data.phone_number;

    const updatedFormData = {
      first_name: data.firstName,
      last_name: data.lastName,
      middle_name: data.middleName,
      email: data.email,
      username: data.username,
      phone_number: phoneWithPrefix,
      gender: data.gender,
      grade: data.grade,
    };
    setFormData(updatedFormData);
    onSubmit(updatedFormData);
  };

  return (
    <CustomDialog
      isOpen={isOpen}
      headerText={isEdit ? t("admin.edit_account") : t("admin.create_account")}
      closeModal={handleClose}
    >
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="flex flex-col gap-4"
      >
        <Input
          label={t("signup.last_name")}
          placeholder={t("signup.enter_surname")}
          errorMsg={errors?.lastName?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.lastName?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("lastName", {
            required: t("signup.last_name") + " " + t("auth.email_required"),
          })}
        />

        <Input
          label={t("signup.name")}
          placeholder={t("signup.enter_name")}
          errorMsg={errors?.firstName?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.firstName?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("firstName", {
            required: t("signup.name") + " " + t("auth.email_required"),
          })}
        />

        <Input
          label={t("team.middle_name")}
          placeholder={t("team.enter_middle_name")}
          errorMsg={errors?.middleName?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.middleName?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          {...register("middleName")}
        />

        <Input
          label={t("student.email")}
          placeholder={t("signup.enter_email")}
          errorMsg={errors?.email?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.email?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="email"
          required
          {...register("email", {
            required: t("auth.email_required"),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t("auth.invalid_email"),
            },
          })}
        />

        <Input
          label={t("auth.iin")}
          placeholder={t("auth.enter_iin")}
          errorMsg={errors?.username?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.username?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("username", {
            required: t("auth.iin_required"),
            maxLength: 12,
            pattern: {
              value: /^[0-9]+$/,
              message: t("auth.invalid_iin"),
            },
          })}
        />

        <Controller
          name="gender"
          control={control}
          rules={{ required: t("signup.gender_required") }}
          render={({ field }) => {
            const genderOptions = [
              { label: t("signup.male"), value: "M" },
              { label: t("signup.female"), value: "F" },
            ];
            return (
              <Select
                label={t("signup.gender")}
                placeholder={t("signup.select_gender")}
                options={genderOptions}
                value={field.value}
                onChange={(option) => field.onChange(option?.value)}
                errorMsg={errors?.gender?.message}
                required
                className="w-full text-sm"
              />
            );
          }}
        />

        <div className="w-full text-sm">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("signup.phone_number")}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="flex flex-row items-end">
            <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg h-10 text-gray-600">
              +7
            </div>
            <Controller
              name="phone_number"
              control={control}
              rules={{
                required: t("signup.phone_number_required"),
                validate: (value) => {
                  const digits = value?.replace(/\D/g, "") || "";
                  if (digits.length !== 10) {
                    return t("auth.invalid_phone");
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Input
                  inputClassName={`bg-white shadow-none outline-1 outline-gray-300 border-l-0 md:rounded-r-lg md:rounded-l-none h-10 flex-1 ${
                    errors?.phone_number?.message &&
                    "ring-error ring-1 border-error"
                  }`}
                  id="phone_number"
                  name="phone_number"
                  className="flex-1"
                  placeholder="7XXXXXXXXX"
                  type="text"
                  value={field.value || ""}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    field.onChange(formatted);
                  }}
                />
              )}
            />
          </div>
          {errors?.phone_number && (
            <span className="text-xs font-semibold text-error">
              {errors.phone_number.message}
            </span>
          )}
        </div>

        {isParticipant && (
          <Controller
            name="grade"
            control={control}
            rules={{
              required: isParticipant ? t("team.grade_required") : false,
            }}
            render={({ field }) => {
              const gradeOptions = [
                { label: t("team.grade_option", { value: 9 }), value: 9 },
                { label: t("team.grade_option", { value: 10 }), value: 10 },
                { label: t("team.grade_option", { value: 11 }), value: 11 },
                { label: t("team.grade_option", { value: 12 }), value: 12 },
              ];
              return (
                <Select
                  label={t("team.grade")}
                  placeholder={t("team.select_grade")}
                  options={gradeOptions}
                  value={field.value}
                  onChange={(option) => field.onChange(option?.value)}
                  errorMsg={errors?.grade?.message}
                  required={isParticipant}
                  className="w-full text-sm"
                />
              );
            }}
          />
        )}

        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.cancel")}
            type="button"
            onClick={handleClose}
          />
          <Button
            text={isEdit ? t("buttons.update") : t("buttons.create")}
            type="submit"
            color="primary"
          />
        </div>
      </form>
    </CustomDialog>
  );
}
